import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import DashboardShell from './DashboardShell';
import SectionHeader from '../components/SectionHeader';
import StatusTabs from '../components/StatusTabs';
import ModuleManagement from './ModuleManagement';
import UserManagement from './UserManagement';
import Pagination from '../components/Pagination';
import { getUserData, refreshUserProfile } from './dashboardService';
import { moduleApi, userModuleApi, certificateApi, assignmentApi } from '../api/api';
import { BASE_URL } from '../api/apiClient';

const MODULE_LIST_PER_PAGE = 6;

function getListFromResponse(data) {
  if (!data || typeof data !== 'object') return [];
  if (Array.isArray(data)) return data;
  const rd = data.returnData;
  if (rd != null && Array.isArray(rd)) return rd;
  if (rd != null && typeof rd === 'object') {
    const listOfItem = rd.list_of_item;
    if (listOfItem && Array.isArray(listOfItem.data)) return listOfItem.data;
    if (Array.isArray(rd.data)) return rd.data;
    const list = listOfItem ?? rd.data ?? rd.certificates ?? rd.assignments;
    if (Array.isArray(list)) return list;
    if (list && Array.isArray(list.data)) return list.data;
  }
  if (Array.isArray(data.data)) return data.data;
  return [];
}

function getModuleMetaFromResponse(data, perPage) {
  const rd = data?.returnData || {};
  const list = rd.list_of_item || rd;
  const meta = list?.meta || rd.meta || list || rd || data;
  const total = Number(meta.total ?? meta.totalCount ?? rd.total ?? 0) || 0;
  const per = Number(meta.per_page ?? meta.perPage ?? perPage) || perPage;
  const current = Number(meta.current_page ?? meta.currentPage ?? 1) || 1;
  const last = meta.last_page ?? meta.lastPage ?? (per > 0 ? Math.max(1, Math.ceil(total / per)) : 1);
  return { current_page: current, last_page: Math.max(1, Number(last) || 1), total, per_page: per };
}

function getDocumentUrl(row) {
  const raw = row?.document_url ?? row?.path ?? row?.certificate ?? row?.document ?? row?.document_path ?? row?.file_url ?? row?.file_path ?? row?.url;
  if (!raw || typeof raw !== 'string') return '';
  if (raw.startsWith('http://') || raw.startsWith('https://')) return raw;
  const root = BASE_URL.replace(/\/api\/?$/, '');
  return raw.startsWith('/') ? root + raw : root + '/' + raw;
}

function getAssignmentDocumentUrl(row) {
  return getDocumentUrl(row);
}

const CERTIFICATE_TABS = [
  { id: 'all', label: 'All' },
  { id: 'pending', label: 'Pending' },
  { id: 'approved', label: 'Approved' },
  { id: 'denied', label: 'Denied' }
];

function getCertificateApprovalStatus(cert) {
  return (cert?.approval_status ?? '').toString().toLowerCase();
}

function formatCertificateApprovalLabel(status) {
  if (!status) return 'Pending';
  return status.charAt(0).toUpperCase() + status.slice(1);
}

function getCertificateStatusClass(status) {
  if (status === 'approved') return 'home-module-card-status-approved';
  if (status === 'denied') return 'home-module-card-status-denied';
  return 'home-module-card-status-pending';
}

function isCertificateApproved(cert) {
  return getCertificateApprovalStatus(cert) === 'approved';
}

function filterCertificatesForUser(list, user) {
  if (!Array.isArray(list) || !user) return [];
  const userId = user.id != null ? Number(user.id) : null;
  const userEmail = (user.email || '').toString().toLowerCase().trim();

  const filtered = list.filter((cert) => {
    const certUserId = cert.user_id ?? cert.userId ?? cert.owner_id ?? cert.assigned_user_id;
    const certEmail = (cert.email ?? cert.user_email ?? cert.username ?? '').toString().toLowerCase().trim();
    if (userId != null && certUserId != null && Number(certUserId) === userId) return true;
    if (userEmail && certEmail && certEmail === userEmail) return true;
    return false;
  });

  const seen = new Set();
  return filtered.filter((cert) => {
    const key = cert.id ?? `${cert.email}-${cert.path ?? cert.created_at}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

function getModuleInitials(m) {
  return ((m?.code || m?.name || '—').replace(/[^a-zA-Z0-9]/g, '').slice(0, 2).toUpperCase()) || '—';
}

const MENU_LABELS = {
  home: 'Home',
  mymodule: 'MyModule',
  certification: 'Certification',
  assignment: 'Assignment',
  usermanagement: 'User Management',
  modulemanagement: 'Module Management',
  helpcentre: 'Help Centre'
};

const SECTION_ITEMS = {
  certification: [{ id: 'c-1', title: 'Pending certifications' }, { id: 'c-2', title: 'Issued certifications' }],
  helpcentre: [{ id: 'hc-1', title: 'Open tickets' }, { id: 'hc-2', title: 'Knowledge base' }]
};

const DEFAULT_SECTION_ITEMS = [{ id: 'overview', title: 'Overview' }];

const Dashboard = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [userData, setUserData] = useState(null);
  const [apiModules, setApiModules] = useState([]);
  const [apiModuleLoading, setApiModuleLoading] = useState(false);
  const [apiModuleError, setApiModuleError] = useState(null);
  const [apiModulePage, setApiModulePage] = useState(1);
  const [apiModuleMeta, setApiModuleMeta] = useState({ current_page: 1, last_page: 1, total: 0, per_page: MODULE_LIST_PER_PAGE });
  const [myCertificates, setMyCertificates] = useState([]);
  const [myCertificatesLoading, setMyCertificatesLoading] = useState(false);
  const [myCertificatesError, setMyCertificatesError] = useState(null);
  const [myAssignments, setMyAssignments] = useState([]);
  const [myAssignmentsLoading, setMyAssignmentsLoading] = useState(false);
  const [myAssignmentsError, setMyAssignmentsError] = useState(null);
  const [enrollingModuleId, setEnrollingModuleId] = useState(null);
  const [enrollError, setEnrollError] = useState(null);
  const [profileReady, setProfileReady] = useState(false);

  const activeMenu = searchParams.get('menu') || 'home';
  const activeTab = searchParams.get('tab') || 'all';
  const certificateTab = CERTIFICATE_TABS.some((t) => t.id === activeTab) ? activeTab : 'all';

  const allowedMenuIds = React.useMemo(() => {
    const r = (userData?.role || '').toString().toLowerCase();
    const isAdmin = r === 'admin' || r === 'administrator';
    if (isAdmin) return ['home', 'usermanagement', 'modulemanagement', 'helpcentre'];
    return ['home', 'mymodule', 'certification', 'assignment', 'helpcentre'];
  }, [userData?.role]);

  useEffect(() => {
    const loadUser = async () => {
      const stored = getUserData();
      if (!stored?.access_token) {
        setUserData(stored);
        setProfileReady(true);
        return;
      }
      const refreshed = await refreshUserProfile();
      setUserData(refreshed);
      setProfileReady(true);
    };
    loadUser();
  }, []);

  useEffect(() => {
    if (!userData || allowedMenuIds.includes(activeMenu)) return;
    setSearchParams(new URLSearchParams({ menu: 'home' }));
  }, [userData, activeMenu, allowedMenuIds, setSearchParams]);

  const fetchMyCertificates = useCallback(async (approvalStatus = 'all') => {
    let user = getUserData();
    if (!user?.access_token) { setMyCertificates([]); return; }
    user = (await refreshUserProfile()) ?? user;
    if (!user?.id) { setMyCertificates([]); return; }
    setMyCertificatesLoading(true); setMyCertificatesError(null);
    try {
      const params = {
        user_id: user.id,
        per_page: 50,
        page: 1,
        paginate: true
      };
      if (approvalStatus && approvalStatus !== 'all') {
        params.approval_status = approvalStatus;
      }
      const { data } = await certificateApi.ilist(params);
      let list = getListFromResponse(data);
      const ok = (data?.status ?? '').toString().toUpperCase() === 'OK';
      list = filterCertificatesForUser(list, user);

      if (ok || list.length > 0) {
        setMyCertificates(Array.isArray(list) ? list : []);
        setMyCertificatesError(null);
      } else {
        setMyCertificates([]);
        setMyCertificatesError(data?.errorMessage || 'Failed to load certificates.');
      }
    } catch (err) {
      setMyCertificates([]); setMyCertificatesError(err.response?.data?.errorMessage || err.message || 'Failed to load certificates.');
    } finally { setMyCertificatesLoading(false); }
  }, []);

  const fetchMyAssignments = useCallback(async () => {
    const user = getUserData();
    if (!user?.access_token) { setMyAssignments([]); return; }
    setMyAssignmentsLoading(true); setMyAssignmentsError(null);
    try {
      const { data } = await assignmentApi.ilist({ per_page: 50, page: 1, paginate: true });
      let list = getListFromResponse(data);
      const ok = (data?.status ?? '').toString().toUpperCase() === 'OK';

      if (ok && list.length === 0 && user.id) {
        const { data: retryData } = await assignmentApi.ilist({
          per_page: 50,
          page: 1,
          paginate: true,
          user_id: user.id,
          assigned_user_id: user.id
        });
        if ((retryData?.status ?? '').toString().toUpperCase() === 'OK') {
          list = getListFromResponse(retryData);
        }
      }

      if (ok || list.length > 0) {
        setMyAssignments(Array.isArray(list) ? list : []);
        setMyAssignmentsError(null);
      } else {
        setMyAssignments([]);
        setMyAssignmentsError(data?.errorMessage || 'Failed to load assignments.');
      }
    } catch (err) {
      setMyAssignments([]); setMyAssignmentsError(err.response?.data?.errorMessage || err.message || 'Failed to load assignments.');
    } finally { setMyAssignmentsLoading(false); }
  }, []);

  const fetchHomeModules = useCallback(async () => {
    const user = getUserData();
    if (!user?.id) return;
    setApiModuleLoading(true); setApiModuleError(null);
    try {
      const { data } = await moduleApi.ilist({ paginate: true, per_page: 6, page: 1, user_id: user.id });
      if (data?.status === 'OK') {
        setApiModules(getListFromResponse(data));
        setApiModuleMeta(getModuleMetaFromResponse(data, 6));
      } else {
        setApiModules([]); setApiModuleError(data?.errorMessage || 'Failed to load modules.');
      }
    } catch (err) {
      setApiModules([]); setApiModuleError(err.response?.data?.errorMessage || err.message || 'Failed to load modules.');
    } finally { setApiModuleLoading(false); }
  }, []);

  const fetchUserModules = useCallback(async (pageNum) => {
    let user = getUserData();
    if (!user?.access_token) return;
    user = (await refreshUserProfile()) ?? user;
    setApiModuleLoading(true); setApiModuleError(null);
    try {
      const params = { page: pageNum, per_page: MODULE_LIST_PER_PAGE, paginate: true };
      let { data } = await userModuleApi.ilist(params);
      let raw = data?.status === 'OK' ? getListFromResponse(data) : [];

      if (data?.status === 'OK' && raw.length === 0 && user.id) {
        const retry = await userModuleApi.ilist({ ...params, user_id: user.id });
        if (retry.data?.status === 'OK') {
          data = retry.data;
          raw = getListFromResponse(retry.data);
        }
      }

      if (data?.status === 'OK') {
        const byModuleId = {};
        raw.forEach((row) => {
          const mid = row.module_id ?? row.module?.id ?? row.id;
          if (mid == null) return;
          const status = (row.status ?? row.module?.status ?? 'open').toString().toLowerCase();
          const isComplete = /complete|completed|finished/.test(status);
          if (!byModuleId[mid] || isComplete) {
            byModuleId[mid] = {
              id: mid,
              code: row.module?.code ?? row.code,
              name: (typeof row.module === 'string' ? row.module : null) ?? row.module?.name ?? row.name ?? row.module_name ?? row.moduleName ?? row.module?.title ?? row.title ?? '',
              progress: row.progress ?? row.module?.progress ?? 0,
              status
            };
          }
        });
        const normalized = Object.values(byModuleId);
        const missingNames = normalized.filter((m) => (m.id != null || m.code) && !m.name);
        if (missingNames.length > 0) {
          try {
            const { data: moduleData } = await moduleApi.ilist({ paginate: true, per_page: 100, page: 1 });
            if (moduleData?.status === 'OK') {
              const allModules = getListFromResponse(moduleData);
              const byId = {};
              const byCode = {};
              allModules.forEach((mod) => {
                const id = mod.id ?? mod.module_id;
                const code = mod.code;
                if (id != null) byId[id] = mod;
                if (code) byCode[code] = mod;
              });
              const enriched = normalized.map((m) => {
                if (m.name) return m;
                const mod = byId[m.id] ?? byCode[m.code];
                const name = mod?.name ?? mod?.title ?? '';
                return name ? { ...m, name } : m;
              });
              setApiModules(enriched);
            } else {
              setApiModules(normalized);
            }
          } catch {
            setApiModules(normalized);
          }
        } else {
          setApiModules(normalized);
        }
        setApiModuleMeta(getModuleMetaFromResponse(data, MODULE_LIST_PER_PAGE));
      } else {
        setApiModules([]); setApiModuleError(data?.errorMessage || 'Failed to load my modules.');
      }
    } catch (err) {
      setApiModules([]); setApiModuleError(err.response?.data?.errorMessage || err.message || 'Failed to load my modules.');
    } finally { setApiModuleLoading(false); }
  }, []);

  useEffect(() => {
    if (!profileReady) return;
    if (activeMenu === 'mymodule') fetchUserModules(apiModulePage);
    if (activeMenu === 'home' && userData?.id) fetchHomeModules();
    if (activeMenu === 'certification') fetchMyCertificates(certificateTab);
    if (activeMenu === 'assignment') fetchMyAssignments();
  }, [profileReady, activeMenu, apiModulePage, certificateTab, fetchUserModules, userData?.id, fetchHomeModules, fetchMyCertificates, fetchMyAssignments]);

  useEffect(() => {
    if (!profileReady) return;
    const role = (userData?.role || '').toString().toLowerCase();
    if (role === 'admin' || role === 'administrator') return;
    fetchMyCertificates('all');
    fetchMyAssignments();
  }, [profileReady, userData?.role, fetchMyCertificates, fetchMyAssignments]);

  const setTab = (nextTab) => {
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set('menu', activeMenu); nextParams.set('tab', nextTab);
    setSearchParams(nextParams);
  };

  const handleEnroll = useCallback(async (moduleId) => {
    let user = getUserData();
    if (!user?.access_token) return;
    user = (await refreshUserProfile()) ?? user;
    if (!user?.id) {
      setEnrollError('Unable to verify your account. Please log out and log in again.');
      return;
    }
    setEnrollError(null);
    setEnrollingModuleId(moduleId);
    try {
      const listParams = { per_page: 100, page: 1, paginate: true };
      let { data: listData } = await userModuleApi.ilist(listParams);
      let list = listData?.status === 'OK' ? getListFromResponse(listData) : [];
      if (listData?.status === 'OK' && list.length === 0) {
        const retry = await userModuleApi.ilist({ ...listParams, user_id: user.id });
        if (retry.data?.status === 'OK') {
          listData = retry.data;
          list = getListFromResponse(retry.data);
        }
      }
      const enrolledModuleIds = [...new Set(list.map((row) => row.module_id ?? row.module?.id).filter(Boolean))];
      if (enrolledModuleIds.length > 0) {
        const alreadyInThis = enrolledModuleIds.some((mid) => Number(mid) === Number(moduleId));
        if (alreadyInThis) {
          setEnrollError('You are already enrolled in this module.');
          setEnrollingModuleId(null);
          return;
        }
        setEnrollError('You can only be enrolled in one module at a time. Complete your current module first.');
        setEnrollingModuleId(null);
        return;
      }
      const { data } = await userModuleApi.iformAction({ form_method: 'save', user_id: user.id, module_id: moduleId });
      if (data?.status === 'OK') {
        setSearchParams(new URLSearchParams({ menu: 'mymodule' }));
      } else {
        setEnrollError(data?.errorMessage || 'Enrollment failed.');
      }
    } catch (err) {
      setEnrollError(err.response?.data?.errorMessage || err.message || 'Enrollment failed.');
    } finally {
      setEnrollingModuleId(null);
    }
  }, [setSearchParams]);

  const title = MENU_LABELS[activeMenu] || 'Home';

  const renderBody = () => {
    if (activeMenu === 'mymodule') {
      const total = apiModuleMeta.total;
      const lastPage = Math.max(apiModuleMeta.last_page, apiModuleMeta.per_page > 0 && total > 0 ? Math.ceil(total / apiModuleMeta.per_page) : 1);
      const currentPage = apiModuleMeta.current_page;
      const from = total > 0 ? (currentPage - 1) * apiModuleMeta.per_page + 1 : 0;
      const to = Math.min(currentPage * apiModuleMeta.per_page, total);
      const isComplete = (m) => /complete|completed|finished/.test((m.status || '').toString().toLowerCase());
      const filteredForTab =
        activeTab === 'active'
          ? apiModules.filter((m) => !isComplete(m))
          : activeTab === 'expired'
            ? apiModules.filter(isComplete)
            : apiModules;
      return (
        <div className="dashboard-content">
          <div className="mymodule-course-list">
            {apiModuleError && <p className="management-error">{apiModuleError}</p>}
            {apiModuleLoading ? (
              <p style={{ color: '#6b7280', marginTop: '0.75rem' }}>Loading modules…</p>
            ) : (
              <>
                {filteredForTab.map((module) => (
                  <div
                    key={module.id}
                    className="mymodule-course-card"
                    onClick={() => navigate(`/modules/${module.id}`)}
                  >
                    <div className="mymodule-course-main">
                      <div className="mymodule-course-logo">HM</div>
                      <div className="mymodule-course-text">
                        <div className="mymodule-course-name">
                          {module.code ?? '—'} — {module.name ?? '—'}
                        </div>
                        <div className="mymodule-course-meta">
                          <span>Documents</span>
                          <span>Videos</span>
                          <span>Quiz</span>
                        </div>
                      </div>
                    </div>
                    <div className="mymodule-course-status">
                      <span className={`mymodule-status-badge ${/complete|completed|finished/.test(module.status || '') ? 'finished' : 'start'}`}>
                        {/complete|completed|finished/.test(module.status || '') ? 'Complete' : 'Open'}
                      </span>
                    </div>
                  </div>
                ))}
                {filteredForTab.length === 0 && !apiModuleLoading && (
                  <p style={{ color: '#6b7280', marginTop: '0.75rem' }}>No modules found for this filter.</p>
                )}
                <Pagination currentPage={currentPage} lastPage={lastPage} onPageChange={setApiModulePage} total={total} from={from} to={to} />
              </>
            )}
          </div>
        </div>
      );
    }

    if (activeMenu === 'certification' && userData) {
      return (
        <div className="dashboard-content">
          <div className="home-modules-section">
            {myCertificatesError && <p className="management-error">{myCertificatesError}</p>}
            {myCertificatesLoading ? (
              <p className="management-empty">Loading certificates…</p>
            ) : myCertificates.length === 0 ? (
              <p className="management-empty">
                You have no certificates yet.
                <button type="button" onClick={() => userData?.id && fetchMyCertificates(certificateTab)} className="management-empty-refresh" style={{ marginLeft: '0.5rem', textDecoration: 'underline', background: 'none', border: 'none', cursor: 'pointer', color: '#2563eb' }}>Retry</button>
              </p>
            ) : (
              <div className="home-module-cards">
                {myCertificates.map((cert, idx) => {
                  const docUrl = getDocumentUrl(cert);
                  const title = cert.title || cert.name || `Certificate #${cert.id ?? idx}`;
                  const initials = (title.replace(/[^a-zA-Z0-9]/g, '').slice(0, 2).toUpperCase()) || 'CT';
                  const approvalStatus = getCertificateApprovalStatus(cert) || 'pending';
                  const approved = isCertificateApproved(cert);
                  const hasFile = !!docUrl;
                  return (
                    <div key={cert.id ?? `cert-${idx}`} className="home-module-card">
                      <div className="home-module-card-icon" aria-hidden>{initials}</div>
                      <div className="home-module-card-body">
                        <h4 className="home-module-card-title">{title}</h4>
                        <p className="home-module-card-types">{cert.description || cert.created_at || 'Certificate'}</p>
                        <span className={`home-module-card-status ${getCertificateStatusClass(approvalStatus)}`}>
                          {formatCertificateApprovalLabel(approvalStatus)}
                        </span>
                      </div>
                      {approved && hasFile ? (
                        <a href={docUrl} download className="home-module-card-open" target="_blank" rel="noopener noreferrer">Download</a>
                      ) : approved ? (
                        <span className="home-module-card-no-file">No document</span>
                      ) : (
                        <span className="home-module-card-no-file">
                          {approvalStatus === 'denied' ? 'Denied' : 'Pending approval'}
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      );
    }

    if (activeMenu === 'assignment' && userData) {
      return (
        <div className="dashboard-content">
          <div className="home-modules-section">
            {myAssignmentsError && <p className="management-error">{myAssignmentsError}</p>}
            {myAssignmentsLoading ? (
              <p className="management-empty">Loading assignments…</p>
            ) : myAssignments.length === 0 ? (
              <p className="management-empty">
                You have no assignments.
                <button type="button" onClick={() => userData?.id && fetchMyAssignments()} className="management-empty-refresh" style={{ marginLeft: '0.5rem', textDecoration: 'underline', background: 'none', border: 'none', cursor: 'pointer', color: '#2563eb' }}>Retry</button>
              </p>
            ) : (
              <div className="home-module-cards">
                {myAssignments.map((row, idx) => {
                  const docUrl = getAssignmentDocumentUrl(row);
                  const title = row.title || 'Assignment';
                  const initials = (title.replace(/[^a-zA-Z0-9]/g, '').slice(0, 2).toUpperCase()) || 'AS';
                  const hasFile = !!docUrl;
                  return (
                    <div key={row.id ?? `assignment-${idx}`} className="home-module-card">
                      <div className="home-module-card-icon" aria-hidden>{initials}</div>
                      <div className="home-module-card-body">
                        <h4 className="home-module-card-title">{row.title || '—'}</h4>
                        <p className="home-module-card-types">{row.description || 'Assignment document'}</p>
                      </div>
                      {hasFile ? (
                        <a href={docUrl} download className="home-module-card-open" target="_blank" rel="noopener noreferrer">Download</a>
                      ) : (
                        <span className="home-module-card-no-file">No document</span>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      );
    }

    if (activeMenu === 'modulemanagement') {
      return <ModuleManagement />;
    }

    if (activeMenu === 'usermanagement') {
      return <UserManagement />;
    }

    if (activeMenu === 'home' && userData) {
      return (
        <div className="dashboard-content">
          <div className="home-modules-section">
            {enrollError && <p className="management-error">{enrollError}</p>}
            {apiModuleLoading ? (
              <p className="management-empty">Loading modules…</p>
            ) : apiModuleError ? (
              <p className="management-error">{apiModuleError}</p>
            ) : apiModules.length === 0 ? (
              <p className="management-empty">No modules found.</p>
            ) : (
              <div className="home-module-cards">
                {apiModules.map((m) => (
                  <div key={m.id} className="home-module-card">
                    <div className="home-module-card-icon" aria-hidden>{getModuleInitials(m)}</div>
                    <div className="home-module-card-body">
                      <h4 className="home-module-card-title">{m.code ?? '—'} — {m.name ?? '—'}</h4>
                      <p className="home-module-card-types">Documents Videos Quiz</p>
                    </div>
                    <button
                      type="button"
                      className="home-module-card-open"
                      onClick={() => handleEnroll(m.id)}
                      disabled={enrollingModuleId === m.id}
                    >
                      {enrollingModuleId === m.id ? 'Enrolling…' : 'Enroll'}
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      );
    }

    if (activeMenu === 'helpcentre') {
      return (
        <div className="dashboard-content">
          <div className="helpcentre-section">
            <h2 className="helpcentre-title">Help Centre</h2>
            <h3 className="helpcentre-subtitle">Get in touch</h3>
            <p className="helpcentre-intro">Need assistance? Contact our support team:</p>
            <div className="helpcentre-contact helpcentre-contact-vertical">
              <div className="helpcentre-contact-item">
                <span className="helpcentre-contact-label">Phone</span>
                <a href="tel:+255625313162" className="helpcentre-contact-value">+255684991881</a>
              </div>
              <div className="helpcentre-contact-item">
                <span className="helpcentre-contact-label">Email</span>
                <a href="mailto:info@bunihub.or.tz" className="helpcentre-contact-value">info@bunihub.or.tz</a>
              </div>
            </div>
          </div>
        </div>
      );
    }

    const items = SECTION_ITEMS[activeMenu] || DEFAULT_SECTION_ITEMS;

    return (
      <div className="dashboard-content">
        <div className="content-cards">
          {items.map((item) => (
            <div key={item.id} className="content-card">
              <div className="card-header">
                <h3 className="card-title">{item.title}</h3>
                <span className="card-count">Info</span>
              </div>
              <p className="card-content">
                This is a placeholder block for {title}. Replace with real data later.
              </p>
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <DashboardShell activeMenuId={activeMenu}>
      <div className="content-wrapper">
        <SectionHeader title={title}>
          {activeMenu === 'mymodule' && <StatusTabs value={activeTab} onChange={setTab} />}
          {activeMenu === 'certification' && (
            <StatusTabs
              value={certificateTab}
              onChange={setTab}
              tabs={CERTIFICATE_TABS}
              ariaLabel="Certificate status filter"
            />
          )}
        </SectionHeader>
        {renderBody()}
      </div>
    </DashboardShell>
  );
};

export default Dashboard;
